import { selectPartnerAccess } from 'legacy/lut/features/partner360/configuration'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { appGetState } from 'legacy/lut/store'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { buildAcknowledgmentStatusView } from '../lib/buildAcknowledgmentStatusView'

import type { AcknowledgmentCommitmentItem, AcknowledgmentOffering } from './types'
import type { AcknowledgmentUpdatedPayload } from './types'
import { useAcknowledgmentActions } from './useAcknowledgmentActions'
import { useAcknowledgmentPartnerInfo } from './useAcknowledgmentPartnerInfo'
import { useAcknowledgmentStatusData } from './useAcknowledgmentStatusData'

type UseAcknowledgmentStatusDialogParams = {
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  onSigned?: (item: AcknowledgmentCommitmentItem) => void
  onNotified?: (item: AcknowledgmentCommitmentItem) => void
  onUpdated?: (payload: AcknowledgmentUpdatedPayload) => void | Promise<void>
}

export const useAcknowledgmentStatusDialog = ({
  partnerId,
  fYear,
  programType,
  onSigned,
  onNotified,
  onUpdated,
}: UseAcknowledgmentStatusDialogParams) => {
  const currentUserEmail = useMemo(() => appGetState().user.email ?? '', [])
  const { isDistributor } = useSelector(selectPartnerAccess)
  const { isPartnerUser, isSuperAdmin } = useUserRoles()

  const { items, isLoading, hasLoaded, error, reload } = useAcknowledgmentStatusData({
    show: true,
    partnerId,
    fYear,
    programType,
  })

  const partnerInfo = useAcknowledgmentPartnerInfo({ partnerId, fYear, programType })

  const viewModel = useMemo(() => buildAcknowledgmentStatusView({ items }), [items])

  const actions = useAcknowledgmentActions({
    reload,
    partnerId,
    fYear,
    programType,
    partnerInfo,
    onSigned,
    onNotified,
    onUpdated,
  })

  return {
    viewModel,
    partnerInfo,
    currentUserEmail,
    isDistributor,
    canManageSigners: !isPartnerUser,
    /* A SuperAdmin may also add a PBM through the Manager form: any HP user, found by email. */
    canAddPbmByEmail: isSuperAdmin,
    isLoading,
    hasLoaded,
    error,
    ...actions,
  }
}
